import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/bi/AppShell";
import { EmptyState } from "@/components/bi/EmptyState";
import { Loading } from "@/components/bi/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { BellRing, Plus, Trash2, Play, Loader2, Pencil, Send } from "lucide-react";

const OPS = [
  ["gt", ">"], ["gte", ">="], ["lt", "<"], ["lte", "<="], ["eq", "="], ["neq", "!="],
  ["pct_up", "% up ≥ (baseline)"],
  ["pct_down", "% down ≥ (baseline)"],
  ["pct_change", "|% change| ≥ (baseline)"],
];
const AGGS = ["sum", "avg", "count", "count_distinct", "min", "max"];

export default function Alerts() {
  const [items, setItems] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [editing, setEditing] = useState(null);   // null | {} (new) | alert
  const [runningId, setRunningId] = useState(null);

  const load = () => api.get("/alerts").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/datasets").then(({ data }) => setDatasets(data)).catch(() => {}); }, []);

  const del = async (id) => { try { await api.delete(`/alerts/${id}`); toast.success("Alert deleted"); load(); } catch { toast.error("Failed"); } };
  const runNow = async (id) => {
    setRunningId(id);
    try {
      const { data } = await api.post(`/alerts/${id}/run`);
      toast[data.breached ? "warning" : "success"](`Current value: ${Number(data.value).toLocaleString()} — ${data.breached ? "condition MET (breached)" : "condition not met"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setRunningId(null); }
  };
  const toggle = async (a, enabled) => {
    try { await api.patch(`/alerts/${a.id}`, { ...pick(a), enabled }); load(); } catch { toast.error("Failed"); }
  };

  const [testingDelivery, setTestingDelivery] = useState(false);
  const testDelivery = async () => {
    setTestingDelivery(true);
    try {
      const { data } = await api.post("/alerts/test-delivery", {}, { timeout: 60000 });
      const r = data.results || {};
      const parts = [];
      if (r.email) parts.push(r.email.ok ? `Email → ${r.email.target} ✓` : `Email ✗ ${r.email.error}`);
      ["slack", "teams"].forEach((ch) => {
        const c = r[ch];
        if (c && c.ok !== null) parts.push(c.ok ? `${ch[0].toUpperCase() + ch.slice(1)} ✓` : `${ch[0].toUpperCase() + ch.slice(1)} ✗ ${c.error}`);
      });
      const anyOk = ["email", "slack", "teams"].some((ch) => r[ch]?.ok);
      toast[anyOk ? "success" : "error"](parts.join("  ·  ") || "Nothing configured — set up email or webhooks in Settings", { duration: 9000 });
      if (!anyOk && !parts.length) toast.info("Configure email (Settings → Email delivery) or Slack/Teams webhooks first");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delivery test failed"); }
    finally { setTestingDelivery(false); }
  };

  return (
    <AppShell title="Alerts" actions={
      <span className="flex items-center gap-2">
        <Button data-testid="test-delivery-btn" variant="outline" onClick={testDelivery} disabled={testingDelivery}
          title="Sends a test alert through every configured channel (email + Slack/Teams) so you can confirm delivery"
          className="bg-[color:var(--surface)]">
          {testingDelivery ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Test delivery
        </Button>
        <Button data-testid="new-alert-btn" onClick={() => setEditing({})} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white"><Plus className="w-4 h-4 mr-1" />New alert</Button>
      </span>
    }>
      <div className="px-5 md:px-8 py-6">
        {items === null ? <Loading label="Loading alerts" /> :
          items.length === 0 ? (
            <EmptyState testid="alerts-empty" icon={BellRing} title="No alerts yet"
              description="Get notified when a measure crosses a threshold — e.g. when sum(revenue) drops below 10,000. Checked automatically on your schedule."
              action={<Button onClick={() => setEditing({})} className="bg-[color:var(--brand-ocean)] text-white"><Plus className="w-4 h-4 mr-1" />Create your first alert</Button>} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {items.map((a) => (
                <div key={a.id} data-testid="alert-card" className="glass-card p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-9 h-9 rounded-xl hero-gradient flex items-center justify-center shrink-0"><BellRing className="w-4 h-4 text-[color:var(--brand-ocean)]" /></div>
                      <h3 className="font-display text-base text-[color:var(--ink)] truncate">{a.name}</h3>
                    </div>
                    <Switch data-testid="alert-toggle" checked={a.enabled} onCheckedChange={(v) => toggle(a, v)} />
                  </div>
                  <div className="text-xs text-[color:var(--ink-3)] mt-3 space-y-1">
                    <div><span className="font-medium text-[color:var(--ink-2)]">{a.agg}({a.field})</span> {OPS.find(([k]) => k === a.operator)?.[1]} {Number(a.threshold).toLocaleString()}</div>
                    <div>Dataset: {a.dataset_name} · every {a.check_interval_minutes} min · {(a.channels || []).join(" + ")}</div>
                    {a.last_checked_at && <div>Last check: {new Date(a.last_checked_at).toLocaleString()} · value {a.last_value != null ? Number(a.last_value).toLocaleString() : "—"}</div>}
                    {a.last_error && <div className="text-[color:var(--danger)]">Error: {a.last_error}</div>}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    {a.last_state && <Badge data-testid="alert-state" className={a.last_state === "breach" ? "bg-[color:rgba(232,93,106,0.14)] text-[color:var(--danger)] border-0" : "bg-[color:rgba(0,194,168,0.15)] text-[color:var(--success)] border-0"}>{a.last_state === "breach" ? "Breached" : "OK"}</Badge>}
                    <div className="flex-1" />
                    <Button data-testid="alert-run-btn" size="sm" variant="ghost" onClick={() => runNow(a.id)} disabled={runningId === a.id} className="text-[color:var(--brand-ocean)]">{runningId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(a)} className="text-[color:var(--ink-2)]"><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(a.id)} className="text-[color:var(--danger)]"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
      <AlertDialog editing={editing} datasets={datasets} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </AppShell>
  );
}

const pick = (a) => ({ name: a.name, dataset_id: a.dataset_id, field: a.field, agg: a.agg, filters: a.filters || [], operator: a.operator, threshold: a.threshold, check_interval_minutes: a.check_interval_minutes, channels: a.channels, recipients: a.recipients || [], enabled: a.enabled, baseline_minutes: a.baseline_minutes || null });

function AlertDialog({ editing, datasets, onClose, onSaved }) {
  const isNew = editing && !editing.id;
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!editing) { setF(null); return; }
    setF(editing.id ? pick(editing) : { name: "", dataset_id: "", field: "", agg: "sum", operator: "lt", threshold: 0, check_interval_minutes: 60, channels: ["in_app"], recipients: [], enabled: true, filters: [] });
  }, [editing]);
  if (!editing || !f) return null;
  const ds = datasets.find((d) => d.id === f.dataset_id);
  const numFields = (ds?.schema || []).map((c) => c.name);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleChannel = (c) => set("channels", f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c]);

  const save = async () => {
    if (!f.name || !f.dataset_id || !f.field) { toast.error("Name, dataset and field are required"); return; }
    setBusy(true);
    try {
      const payload = { ...f, threshold: Number(f.threshold), check_interval_minutes: Number(f.check_interval_minutes) || 60 };
      if (isNew) await api.post("/alerts", payload);
      else await api.patch(`/alerts/${editing.id}`, payload);
      toast.success(isNew ? "Alert created" : "Alert updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><BellRing className="w-5 h-5 text-[color:var(--brand-ocean)]" />{isNew ? "New alert" : "Edit alert"}</DialogTitle>
          <DialogDescription>Notify me when a measure crosses a threshold. Email delivery requires SMTP settings (admin).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label>Alert name</Label><Input data-testid="alert-name" className="mt-1.5" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Low revenue warning" /></div>
          <div><Label>Dataset</Label>
            <Select value={f.dataset_id} onValueChange={(v) => { set("dataset_id", v); set("field", ""); }}>
              <SelectTrigger data-testid="alert-dataset" className="mt-1.5"><SelectValue placeholder="Pick a dataset" /></SelectTrigger>
              <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div><Label className="text-xs">Agg</Label>
              <Select value={f.agg} onValueChange={(v) => set("agg", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{AGGS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Field</Label>
              <Select value={f.field} onValueChange={(v) => set("field", v)}>
                <SelectTrigger data-testid="alert-field" className="mt-1.5"><SelectValue placeholder="field" /></SelectTrigger>
                <SelectContent>{numFields.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Condition</Label>
              <Select value={f.operator} onValueChange={(v) => set("operator", v)}>
                <SelectTrigger data-testid="alert-operator" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{OPS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Threshold</Label><Input data-testid="alert-threshold" type="number" className="mt-1.5" value={f.threshold} onChange={(e) => set("threshold", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Check every (minutes)</Label><Input data-testid="alert-interval" type="number" min={1} max={1440} className="mt-1.5" value={f.check_interval_minutes} onChange={(e) => set("check_interval_minutes", e.target.value)} /></div>
            <div>
              <Label className="text-xs">Channels</Label>
              <div className="flex gap-3 mt-2.5">
                <label className="flex items-center gap-1.5 text-sm"><Switch data-testid="alert-channel-inapp" checked={f.channels.includes("in_app")} onCheckedChange={() => toggleChannel("in_app")} />In-app</label>
                <label className="flex items-center gap-1.5 text-sm"><Switch data-testid="alert-channel-email" checked={f.channels.includes("email")} onCheckedChange={() => toggleChannel("email")} />Email</label>
                <label className="flex items-center gap-1.5 text-sm"><Switch data-testid="alert-channel-slack" checked={f.channels.includes("slack")} onCheckedChange={() => toggleChannel("slack")} />Slack</label>
                <label className="flex items-center gap-1.5 text-sm"><Switch data-testid="alert-channel-teams" checked={f.channels.includes("teams")} onCheckedChange={() => toggleChannel("teams")} />Teams</label>
              </div>
            </div>
          </div>
          {f.channels.includes("email") && (
            <div><Label className="text-xs">Email recipients (comma-separated)</Label>
              <Input data-testid="alert-recipients" className="mt-1.5" placeholder="a@x.com, b@y.com" value={(f.recipients || []).join(", ")} onChange={(e) => set("recipients", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
            </div>
          )}
          {(f.operator || "").startsWith("pct_") && (
            <div className="rounded-md bg-[color:rgba(43,78,255,0.06)] border border-[color:var(--brand-ocean)]/20 p-2.5">
              <Label className="text-xs">Baseline window (minutes)</Label>
              <Input
                data-testid="alert-baseline-minutes"
                type="number" min={1} max={44640}
                placeholder={`Default: ${f.check_interval_minutes || 60} (one check interval)`}
                className="mt-1.5 bg-[color:var(--surface)]"
                value={f.baseline_minutes || ""}
                onChange={(e) => set("baseline_minutes", e.target.value ? Number(e.target.value) : null)}
              />
              <p className="text-[10px] text-[color:var(--ink-3)] mt-1">
                Current value is compared to the most recent history point older than this window.
                First few checks after creating a %-change alert will not fire until history builds up.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="alert-save-btn" onClick={save} disabled={busy} className="bg-[color:var(--brand-ocean)] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}{isNew ? "Create alert" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
